import { Box, Chip, Grid, useMediaQuery } from '@mui/material'
import {  useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom'
import { IHeadCell } from '../../../../shared/interfaces/table-display.model';
import { TableDisplay } from '../../../presentation-components/miscellaneous-components/table-display';
import gaiaTheme from '../../../../root-components/theme';
import { LoadingPage } from '../../loading-page';
import { PageOuterContainer } from '../../../../root-components/layouts/page-outer-container';
import { PageHeading } from '../../../presentation-components/display-elements-components/page-heading';
import { IconName } from '../../../../shared/interfaces/icon-picker.model';
import RenewableEnergyCertificateService from '../../../../shared/services/renewable-energy-certificate/renewable-energy-certificate.service';
import { IReneweableEnergyCertificate } from '../../../../shared/services/renewable-energy-certificate/renewable-energy-certificate.model';
import { SearchBar } from '../../../presentation-components/display-elements-components/search-bar';
import moment from 'moment';
import { useDebounce } from '../../../../hooks/use-debounce-hook';

export const OrganisationRenewableEnergyCertificatesPage = () => {
  const navigate = useNavigate();
  //Services
  const renewableEnergyCertificateService = new RenewableEnergyCertificateService()
  //Context
  //State
  const [renewableEnergyCertificates, setRenewableEnergyCertificates] = useState<IReneweableEnergyCertificate[]>()
  const [searchQuery, setSearchQuery] = useState('')
  //Table State
  const [selected, setSelected] = useState<string[]>([]);
  const [inputPage, setInputPage] = useState(0);
  const [totalRecords, setTotalRecords] = useState(0)
  const [perPage, setPerPage] = useState(10)
  const [change, setChange] = useState<number>(0)
  const [orderBy, setOrderBy] = useState<string>()
  const [order, setOrder] = useState<'asc' | 'desc'>()
  //Loading State
  const [hasPageMounted, setHasPageMounted] = useState<boolean>(false)
  const [isLoading, setIsLoading] = useState<boolean>(true)
  //Hooks
  const debouncedSearchQuery = useDebounce(searchQuery, 500);
  //Ref
  const requestId = useRef<number>(0)
  //Layout
  const isMobileMode = useMediaQuery(gaiaTheme.breakpoints.down('md'));

  //Use Effects
  useEffect(() => {
    getRenewableEnergyCertificates()
  }, [inputPage, perPage, change])

  const getRenewableEnergyCertificates = () => {
    requestId.current++
    const currentRequestId = requestId.current
    renewableEnergyCertificateService.getAllWithPagination(debouncedSearchQuery, inputPage, perPage, orderBy, order)
      .then(response => {
        if (currentRequestId === requestId.current) {
          setIsLoading(false)
          setTotalRecords(response.count);
          setRenewableEnergyCertificates(response.rows)
        }
      })
      .catch(err => {
        if (currentRequestId === requestId.current) {
          setIsLoading(false)
        }
        console.log(err)
      })
  }

  useEffect(() => {
    if (hasPageMounted) {
      if (inputPage === 0) {
        setChange(change + 1)
      } else {
        setInputPage(0)
      }
    }
    setHasPageMounted(true)
  }, [order, orderBy, debouncedSearchQuery])

  //Table Contents
  const headCells: IHeadCell[] = [
    { id: 'certificateNumber', numeric: false, label: 'Certificate Number', sortable: true, isHeading: true },
    { id: 'certificateType', numeric: false, label: 'Certificate Type', sortable: true },
    { id: 'generationPeriod', numeric: false, label: 'Generation Period', sortable: true },
    { id: 'energyMwh', numeric: false, label: 'Energy (MWh)', sortable: true },
    { id: 'retired', numeric: false, label: 'Status', sortable: false },
  ];

  const rows = !renewableEnergyCertificates ? [] : renewableEnergyCertificates.map(renewableEnergyCertificate => {
    return {
      id: renewableEnergyCertificate.id,
      certificateNumber: renewableEnergyCertificate.certificateNumber,
      generationPeriod: moment(renewableEnergyCertificate.generationPeriod).format('MM-YYYY'),
      certificateType: renewableEnergyCertificate.certificateType,
      energyMwh: renewableEnergyCertificate.energyMwh,
      retired: (
        renewableEnergyCertificate.retired ?
          <Chip label='Retired' color='error' variant='outlined' sx={{ width: '130px' }} /> :
          renewableEnergyCertificate.recInUse ?
            <Chip label='Partially Used' color='warning' variant='outlined' sx={{ width: '130px' }} /> :
            < Chip label='Active' color='success' variant='outlined' sx={{ width: '130px' }
            } />
      )
    }
  });

  // Table Event Handlers
  const handleOrderChange = (orderBy: string, order: 'asc' | 'desc',) => {
    setOrderBy(orderBy)
    setOrder(order)
  };

  const handleSelected = (selectedRowId: string[]) => {
    if (selectedRowId[0] !== undefined) {
      navigate(`/organisation/renewable-energy-certificates/${selectedRowId[0]}`)
    }
  }

  return (
    <PageOuterContainer>
      <PageHeading
        title={'Renewable Energy Certificates'}
        includeActionButton={true}
        actionButtonIcon={IconName.Add}
        actionButtonHandler={() => navigate('/organisation/renewable-energy-certificates/add-new-renewable-energy-certificate')}
        actionButtonText={`Add Renewable Energy Certificate`}
      />
      <Box sx={{ width: '100%' }}>
        <Grid container sx={{ mb: 2 }}>
          <Grid
            item
            xl={4} lg={4} md={6} sm={12} xs={12}
            sx={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'flex-start',
              width: '100%'
            }}>
            <SearchBar searchQuery={searchQuery} setSearchQuery={setSearchQuery} />
          </Grid>
        </Grid>
        <LoadingPage pageReady={!isLoading}>
          {renewableEnergyCertificates &&
            <TableDisplay
              onOrderChange={handleOrderChange}
              isMobileMode={isMobileMode}
              headCells={headCells}
              rows={rows}
              handleSelected={handleSelected}
              selected={selected}
              setSelected={setSelected}
              inputPage={inputPage}
              setInputPage={setInputPage}
              totalRecords={totalRecords}
              perPage={perPage}
              setPerPage={setPerPage} />}
        </LoadingPage>
      </Box>
    </PageOuterContainer>
  )
}